import { WifiOff, CloudOff, RefreshCw } from 'lucide-react';
import { useOffline } from '@/contexts/OfflineContext';
import { cn } from '@/lib/utils';

/**
 * OfflineIndicator - Shows when user is offline
 * 
 * Displays a banner when offline and shows queued sync count
 */
const OfflineIndicator = ({ className }) => {
  const { isOnline, isOfflineReady, queuedRequests } = useOffline();

  // Don't show if online and no queued requests
  if (isOnline && queuedRequests === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50",
        "px-4 py-2 rounded-full shadow-lg",
        "flex items-center gap-2 text-sm font-medium",
        "transition-all duration-300",
        !isOnline ? "bg-amber-500 text-white" : "bg-emerald-500 text-white",
        className
      )}
      data-testid="offline-indicator"
    >
      {!isOnline ? (
        <>
          <WifiOff className="h-4 w-4" />
          <span>Offline Mode</span>
          {isOfflineReady && (
            <span className="text-xs opacity-80">• Data saved locally</span>
          )}
        </>
      ) : queuedRequests > 0 ? (
        <>
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Syncing {queuedRequests} change{queuedRequests !== 1 ? 's' : ''}...</span>
        </>
      ) : null}
    </div>
  );
};

/**
 * OfflineBadge - Small badge for header/nav showing offline status
 */
export const OfflineBadge = ({ className }) => {
  const { isOnline, queuedRequests } = useOffline();

  if (isOnline && queuedRequests === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
        !isOnline ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700",
        className
      )}
    >
      {!isOnline ? (
        <>
          <CloudOff className="h-3 w-3" />
          <span>Offline</span>
        </>
      ) : (
        <>
          <RefreshCw className="h-3 w-3 animate-spin" />
          <span>{queuedRequests}</span>
        </>
      )}
    </div>
  );
};

export default OfflineIndicator;
